import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./signup.css";
import { useNavigate } from "react-router";
import { getReturnUrl, buildUrlWithReturnUrl } from "../../utils/auth";

const Signup = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [healthProviderData, setHealthProviderData] = useState({
    firstName: "",
    lastName: "",
    organizationName: "",
    specialty: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [patientData, setPatientData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    gender: "",
    email: "",
    dateOfBirth: { month: "", date: "", year: "" },
    password: "",
    confirmPassword: "",
  });
  const [healthProviderErrors, setHealthProviderErrors] = useState({});
  const [patientErrors, setPatientErrors] = useState({});
  const [healthProviderTouched, setHealthProviderTouched] = useState({
    firstName: false,
    lastName: false,
    organizationName: false,
    specialty: false,
    phone: false,
    email: false,
    password: false,
    confirmPassword: false,
  });
  const [patientTouched, setPatientTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    gender: false,
    email: false,
    month: false,
    date: false,
    year: false,
    password: false,
    confirmPassword: false,
  });
  const [showHealthProviderPassword, setShowHealthProviderPassword] =
    useState(false);
  const [
    showHealthProviderConfirmPassword,
    setShowHealthProviderConfirmPassword,
  ] = useState(false);
  const [showPatientPassword, setShowPatientPassword] = useState(false);
  const [showPatientConfirmPassword, setShowPatientConfirmPassword] =
    useState(false);
  const [healthProviderLoading, setHealthProviderLoading] = useState(false);
  const [patientLoading, setPatientLoading] = useState(false);
  const [healthProviderApiError, setHealthProviderApiError] = useState(null);
  const [patientApiError, setPatientApiError] = useState(null);
  const [healthProviderApiSuccess, setHealthProviderApiSuccess] =
    useState(null);
  const [patientApiSuccess, setPatientApiSuccess] = useState(null);
  const healthProviderEmailRef = useRef(null);
  const patientEmailRef = useRef(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/register";

  const handleRoleSelect = (role) => setSelectedRole(role);

  const handleHealthProviderChange = (e) => {
    const { name, value } = e.target;
    updateHealthProviderForm(name, value);
  };

  const handleHealthProviderBlur = (e) => {
    const { name } = e.target;
    setHealthProviderTouched((prev) => ({ ...prev, [name]: true }));
    validateHealthProvider();
  };

  const handleHealthProviderPhoneChange = (value) => {
    updateHealthProviderForm("phone", value || "");
  };

  const handleHealthProviderPhoneBlur = () => {
    setHealthProviderTouched((prev) => ({ ...prev, phone: true }));
    validateHealthProvider();
  };

  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    updatePatientForm(name, value);
  };

  const handlePatientBlur = (e) => {
    const { name } = e.target;
    setPatientTouched((prev) => ({ ...prev, [name]: true }));
    validatePatient();
  };

  const handlePatientPhoneChange = (value) => {
    updatePatientForm("phone", value || "");
  };

  const handlePatientPhoneBlur = () => {
    setPatientTouched((prev) => ({ ...prev, phone: true }));
    validatePatient();
  };

  const handlePatientDateChange = (field, value) => {
    let sanitizedValue = value;

    if (field === "date") {
      sanitizedValue = value.slice(0, 2);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        updatePatientForm("dateOfBirth", {
          ...patientData.dateOfBirth,
          [field]: sanitizedValue,
        });
      }
    } else if (field === "year") {
      sanitizedValue = value.slice(0, 4);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        updatePatientForm("dateOfBirth", {
          ...patientData.dateOfBirth,
          [field]: sanitizedValue,
        });
      }
    } else {
      updatePatientForm("dateOfBirth", {
        ...patientData.dateOfBirth,
        [field]: sanitizedValue,
      });
    }
  };

  const handlePatientDateBlur = (field) => {
    setPatientTouched((prev) => ({ ...prev, [field]: true }));
    validatePatient();
  };

  const updateHealthProviderForm = (name, value) => {
    setHealthProviderData((prev) => ({ ...prev, [name]: value }));
  };

  const updatePatientForm = (name, value) => {
    if (name === "dateOfBirth") {
      setPatientData((prev) => ({
        ...prev,
        dateOfBirth: value,
      }));
    } else {
      setPatientData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateHealthProvider = () => {
    const newErrors = {};
    const touched = healthProviderTouched;

    if (touched.firstName && !healthProviderData.firstName)
      newErrors.firstName = "First Name is required";
    if (touched.lastName && !healthProviderData.lastName)
      newErrors.lastName = "Last Name is required";
    if (touched.organizationName && !healthProviderData.organizationName)
      newErrors.organizationName = "Organization Name is required";
    if (touched.specialty && !healthProviderData.specialty)
      newErrors.specialty = "Specialty is required";

    if (touched.phone) {
      if (!healthProviderData.phone) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneNum = healthProviderData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }
    }

    if (touched.email) {
      if (!healthProviderData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(healthProviderData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (touched.password) {
      if (!healthProviderData.password) {
        newErrors.password = "Password is required";
      } else if (
        !/^(?=.*[A-Z])(?=.*[\W_]).{8,}$/.test(healthProviderData.password)
      ) {
        newErrors.password =
          "Password must contain at least 8 characters, one uppercase letter, and one symbol";
      }
    }

    if (touched.confirmPassword) {
      if (!healthProviderData.confirmPassword) {
        newErrors.confirmPassword = "Confirm Password is required";
      } else if (
        healthProviderData.password &&
        healthProviderData.password !== healthProviderData.confirmPassword
      ) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setHealthProviderErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const validatePatient = () => {
    const newErrors = {};
    const touched = patientTouched;

    if (touched.firstName && !patientData.firstName)
      newErrors.firstName = "First Name is required";
    if (touched.lastName && !patientData.lastName)
      newErrors.lastName = "Last Name is required";
    if (touched.gender && !patientData.gender)
      newErrors.gender = "Gender is required";

    if (touched.phone) {
      if (!patientData.phone) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneNum = patientData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }
    }

    if (touched.email) {
      if (!patientData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(patientData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    const { month, date, year } = patientData.dateOfBirth;
    const currentYear = new Date().getFullYear();

    if (touched.month && !month) {
      newErrors.month = "Month is required";
    }

    if (touched.date) {
      if (!date) {
        newErrors.date = "Day is required";
      } else {
        const dateNum = Number(date);
        if (isNaN(dateNum) || dateNum < 1 || dateNum > 31) {
          newErrors.date = "Day is invalid";
        }
      }
    }

    if (touched.year) {
      if (!year) {
        newErrors.year = "Year is required";
      } else if (year.length === 4) {
        const yearNum = Number(year);
        if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
          newErrors.year = "Year is invalid";
        }
      }
    }

    if (touched.password) {
      if (!patientData.password) {
        newErrors.password = "Password is required";
      } else if (!/^(?=.*[A-Z])(?=.*[\W_]).{8,}$/.test(patientData.password)) {
        newErrors.password =
          "Password must contain at least 8 characters, one uppercase letter, and one symbol";
      }
    }

    if (touched.confirmPassword) {
      if (!patientData.confirmPassword) {
        newErrors.confirmPassword = "Confirm Password is required";
      } else if (
        patientData.password &&
        patientData.password !== patientData.confirmPassword
      ) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setPatientErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const isHealthProviderIncomplete = () => {
    return Object.values(healthProviderData).some((value) => value === "");
  };

  const isPatientIncomplete = () => {
    const { dateOfBirth, ...otherFields } = patientData;
    return (
      Object.values(otherFields).some((value) => value === "") ||
      Object.values(dateOfBirth).some((value) => value === "")
    );
  };

  useEffect(() => {
    if (selectedRole === "healthProvider") validateHealthProvider();
    if (selectedRole === "patient") validatePatient();
  }, [
    healthProviderData,
    patientData,
    healthProviderTouched,
    patientTouched,
    selectedRole,
  ]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roleFromUrl = urlParams.get("role");
    const fromOtp = urlParams.get("fromOtp");

    if (fromOtp === "true" && roleFromUrl) {
      setSelectedRole(roleFromUrl);
      setTimeout(() => {
        if (roleFromUrl === "healthProvider") {
          console.log("Health Provider ref:", healthProviderEmailRef.current);
          healthProviderEmailRef.current?.foci();
        } else if (roleFromUrl === "patient") {
          console.log("Patient ref:", patientEmailRef.current);
          patientEmailRef.current?.focus();
        }
      }, 100);
    } else if (fromOtp === "true" && !roleFromUrl) {
      setSelectedRole("healthProvider");
      setTimeout(() => {
        console.log(
          "Default Health Provider ref:",
          healthProviderEmailRef.current
        );
        healthProviderEmailRef.current?.focus();
      }, 100);
    }
  }, []);

  const handleHealthProviderSubmit = async (e) => {
    e.preventDefault();
    setHealthProviderTouched({
      firstName: true,
      lastName: true,
      organizationName: true,
      specialty: true,
      phone: true,
      email: true,
      password: true,
      confirmPassword: true,
    });

    if (!validateHealthProvider()) return;

    setHealthProviderLoading(true);
    setHealthProviderApiError(null);
    setHealthProviderApiSuccess(null);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role: "provider",
          first_name: healthProviderData.firstName,
          last_name: healthProviderData.lastName,
          organization_name: healthProviderData.organizationName,
          specialty: healthProviderData.specialty,
          phone: healthProviderData.phone,
          email: healthProviderData.email,
          password: healthProviderData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Registration failed");
      }

      setHealthProviderApiSuccess(
        "Registration successful! Please check your email for OTP verification."
      );
      setHealthProviderData({
        firstName: "",
        lastName: "",
        organizationName: "",
        specialty: "",
        phone: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      setHealthProviderTouched({
        firstName: false,
        lastName: false,
        organizationName: false,
        specialty: false,
        phone: false,
        email: false,
        password: false,
        confirmPassword: false,
      });
      // Check for return URL in query params
      const returnUrl = getReturnUrl(window.location.search);
      
      // Build OTP URL with return URL if available
      const baseOtpUrl = `/otp?email=${encodeURIComponent(
        healthProviderData.email
      )}&role=healthProvider&from=signup&next=verify`;
      
      const otpUrl = buildUrlWithReturnUrl(baseOtpUrl, returnUrl);
      window.location.href = otpUrl;
    } catch (error) {
      setHealthProviderApiError(error.message);
    } finally {
      setHealthProviderLoading(false);
    }
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setPatientTouched({
      firstName: true,
      lastName: true,
      phone: true,
      gender: true,
      email: true,
      month: true,
      date: true,
      year: true,
      password: true,
      confirmPassword: true,
    });

    if (!validatePatient()) return;

    setPatientLoading(true);
    setPatientApiError(null);
    setPatientApiSuccess(null);

    try {
      const dob = `${
        patientData.dateOfBirth.year
      }-${patientData.dateOfBirth.month.padStart(
        2,
        "0"
      )}-${patientData.dateOfBirth.date.padStart(2, "0")}`;

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role: "patient",
          first_name: patientData.firstName,
          last_name: patientData.lastName,
          phone: patientData.phone,
          email: patientData.email,
          password: patientData.password,
          gender: patientData.gender,
          date_of_birth: dob,
          home_address: "Not provided",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Registration failed");
      }

      setPatientApiSuccess(
        "Registration successful! Please check your email for OTP verification."
      );
      setPatientData({
        firstName: "",
        lastName: "",
        phone: "",
        gender: "",
        email: "",
        dateOfBirth: { month: "", date: "", year: "" },
        password: "",
        confirmPassword: "",
      });
      setPatientTouched({
        firstName: false,
        lastName: false,
        phone: false,
        gender: false,
        email: false,
        month: false,
        date: false,
        year: false,
        password: false,
        confirmPassword: false,
      });
      // Check for return URL in query params
      const returnUrl = getReturnUrl(window.location.search);
      
      // Build OTP URL with return URL if available
      const baseOtpUrl = `/otp?email=${encodeURIComponent(
        patientData.email
      )}&role=patient&from=signup`;
      
      const otpUrl = buildUrlWithReturnUrl(baseOtpUrl, returnUrl);
      window.location.href = otpUrl;
    } catch (error) {
      setPatientApiError(error.message);
    } finally {
      setPatientLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const roleButtonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95 },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
    exit: { opacity: 0, x: 100, transition: { duration: 0.4, ease: "easeIn" } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <motion.div
      className="signup-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="signup-card" variants={containerVariants}>
        <motion.div className="logo" variants={itemVariants}>
          <a href="/">
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
              alt="Direct by Syndicate Bio"
            />
          </a>
        </motion.div>
        <div className="content-wrapper">
          <motion.h2 variants={itemVariants}>Create your account</motion.h2>
          <motion.p variants={itemVariants}>
            Join Direct by Syndicate Bio to order and track advanced diagnostic
            tests with ease.
          </motion.p>
          <motion.div
            className="role-buttons"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
          >
            <motion.button
              className={`role-button health-provider ${
                selectedRole === "healthProvider" ? "active" : ""
              }`}
              onClick={() => handleRoleSelect("healthProvider")}
              variants={roleButtonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/doctor-icon.svg`}
                alt=""
                className="role-icon"
              />{" "}
              Health Provider
            </motion.button>
            <motion.button
              className={`role-button patient ${
                selectedRole === "patient" ? "active" : ""
              }`}
              onClick={() => handleRoleSelect("patient")}
              variants={roleButtonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/patient-icon.svg`}
                alt=""
                className="role-icon"
              />{" "}
              Patient
            </motion.button>
          </motion.div>
          <AnimatePresence>
            {selectedRole === "healthProvider" && (
              <motion.form
                key="healthProvider"
                className="form health-provider"
                variants={formVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onSubmit={handleHealthProviderSubmit}
              >
                <motion.div
                  variants={itemVariants}
                  className="form-group name-row"
                >
                  <div className="name-input">
                    <label>First Name</label>
                    <input
                      name="firstName"
                      value={healthProviderData.firstName}
                      onChange={handleHealthProviderChange}
                      onBlur={handleHealthProviderBlur}
                      placeholder="Enter first name"
                    />
                    {healthProviderErrors.firstName && (
                      <span className="error">
                        {healthProviderErrors.firstName}
                      </span>
                    )}
                  </div>
                  <div className="name-input">
                    <label>Last Name</label>
                    <input
                      name="lastName"
                      value={healthProviderData.lastName}
                      onChange={handleHealthProviderChange}
                      onBlur={handleHealthProviderBlur}
                      placeholder="Enter last name"
                    />
                    {healthProviderErrors.lastName && (
                      <span className="error">
                        {healthProviderErrors.lastName}
                      </span>
                    )}
                  </div>
                </motion.div>
                <motion.div variants={itemVariants} className="form-group">
                  <label>Organization Name</label>
                  <input
                    name="organizationName"
                    value={healthProviderData.organizationName}
                    onChange={handleHealthProviderChange}
                    onBlur={handleHealthProviderBlur}
                    placeholder="Enter your organization name"
                  />
                  {healthProviderErrors.organizationName && (
                    <span className="error">
                      {healthProviderErrors.organizationName}
                    </span>
                  )}
                </motion.div>
                <motion.div variants={itemVariants} className="form-group">
                  <label>Specialty</label>
                  <select
                    name="specialty"
                    value={healthProviderData.specialty}
                    onChange={handleHealthProviderChange}
                    onBlur={handleHealthProviderBlur}
                  >
                    <option value="">Select from dropdown</option>
                    <option value="Radiation Oncologist">Radiation Oncologist</option>
                    <option value="Haematologist">Haematologist</option>
                    <option value="Anatomic Pathologist">Anatomic Pathologist</option>
                    <option value="Maxillofacial Surgeon">Maxillofacial Surgeon</option>
                    <option value="ENT Surgeon">ENT Surgeon</option>
                    <option value="Ophthalmologist">Ophthalmologist</option>
                    <option value="Orthopaedic Surgeon">Orthopaedic Surgeon</option>
                    <option value="Urologist">Urologist</option>
                    <option value="Gastroenterologist">Gastroenterologist</option>
                    <option value="Hepatobiliary Surgeon">Hepatobiliary Surgeon</option>
                    <option value="Dermatologist">Dermatologist</option>
                  </select>
                  {healthProviderErrors.specialty && (
                    <span className="error">
                      {healthProviderErrors.specialty}
                    </span>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="form-group phone"
                >
                  <label>Phone Number</label>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="NG"
                    value={healthProviderData.phone}
                    onChange={handleHealthProviderPhoneChange}
                    onBlur={handleHealthProviderPhoneBlur}
                    placeholder="Enter phone number"
                    maxLength={17}
                  />
                  {healthProviderErrors.phone && (
                    <span className="error">{healthProviderErrors.phone}</span>
                  )}
                </motion.div>
                <motion.div variants={itemVariants} className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={healthProviderData.email}
                    onChange={handleHealthProviderChange}
                    onBlur={handleHealthProviderBlur}
                    placeholder="Enter your email address"
                    ref={healthProviderEmailRef}
                  />
                  {healthProviderErrors.email && (
                    <span className="error">{healthProviderErrors.email}</span>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="form-group password-group"
                >
                  <label>Password</label>
                  <div className="password-wrapper">
                    <input
                      type={showHealthProviderPassword ? "text" : "password"}
                      name="password"
                      value={healthProviderData.password}
                      onChange={handleHealthProviderChange}
                      onBlur={handleHealthProviderBlur}
                      placeholder="Enter your password"
                    />
                    <span
                      className={`toggle-password ${
                        healthProviderData.password ? "visible" : "hidden"
                      }`}
                      onClick={() =>
                        setShowHealthProviderPassword(
                          !showHealthProviderPassword
                        )
                      }
                    >
                      {showHealthProviderPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                  {healthProviderErrors.password && (
                    <span className="error">
                      {healthProviderErrors.password}
                    </span>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="form-group password-group"
                >
                  <label>Confirm Password</label>
                  <div className="password-wrapper">
                    <input
                      type={
                        showHealthProviderConfirmPassword ? "text" : "password"
                      }
                      name="confirmPassword"
                      value={healthProviderData.confirmPassword}
                      onChange={handleHealthProviderChange}
                      onBlur={handleHealthProviderBlur}
                      placeholder="Re-enter your password"
                    />
                    <span
                      className={`toggle-password ${
                        healthProviderData.confirmPassword
                          ? "visible"
                          : "hidden"
                      }`}
                      onClick={() =>
                        setShowHealthProviderConfirmPassword(
                          !showHealthProviderConfirmPassword
                        )
                      }
                    >
                      {showHealthProviderConfirmPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                  {healthProviderErrors.confirmPassword && (
                    <span className="error">
                      {healthProviderErrors.confirmPassword}
                    </span>
                  )}
                </motion.div>
                {healthProviderApiError && (
                  <motion.p className="error-message" variants={itemVariants}>
                    {healthProviderApiError}
                  </motion.p>
                )}
                {healthProviderApiSuccess && (
                  <motion.p className="success-message" variants={itemVariants}>
                    {healthProviderApiSuccess}
                  </motion.p>
                )}
                <motion.div variants={itemVariants}>
                  <motion.button
                    type="submit"
                    className={`continue-button ${
                      healthProviderLoading ||
                      isHealthProviderIncomplete() ||
                      Object.values(healthProviderErrors).some((error) => error)
                        ? "disabled"
                        : ""
                    }`}
                    disabled={
                      healthProviderLoading ||
                      isHealthProviderIncomplete() ||
                      Object.values(healthProviderErrors).some((error) => error)
                    }
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {healthProviderLoading
                      ? "Registering..."
                      : "Continue with email"}
                  </motion.button>
                </motion.div>
              </motion.form>
            )}
            {selectedRole === "patient" && (
              <motion.form
                key="patient"
                className="form patient"
                variants={formVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onSubmit={handlePatientSubmit}
              >
                <motion.div
                  variants={itemVariants}
                  className="form-group name-row"
                >
                  <div className="name-input">
                    <label>First Name</label>
                    <input
                      name="firstName"
                      value={patientData.firstName}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter first name"
                    />
                    {patientErrors.firstName && (
                      <span className="error">{patientErrors.firstName}</span>
                    )}
                  </div>
                  <div className="name-input">
                    <label>Last Name</label>
                    <input
                      name="lastName"
                      value={patientData.lastName}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter last name"
                    />
                    {patientErrors.lastName && (
                      <span className="error">{patientErrors.lastName}</span>
                    )}
                  </div>
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="form-group phone"
                >
                  <label>Phone Number</label>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="NG"
                    value={patientData.phone}
                    onChange={handlePatientPhoneChange}
                    onBlur={handlePatientPhoneBlur}
                    placeholder="Enter phone number"
                    maxLength={17}
                  />
                  {patientErrors.phone && (
                    <span className="error">{patientErrors.phone}</span>
                  )}
                </motion.div>
                <motion.div variants={itemVariants} className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={patientData.gender}
                    onChange={handlePatientChange}
                    onBlur={handlePatientBlur}
                  >
                    <option value="">Select from dropdown</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {patientErrors.gender && (
                    <span className="error">{patientErrors.gender}</span>
                  )}
                </motion.div>
                <motion.div variants={itemVariants} className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={patientData.email}
                    onChange={handlePatientChange}
                    onBlur={handlePatientBlur}
                    placeholder="Enter your email address"
                    ref={patientEmailRef}
                  />
                  {patientErrors.email && (
                    <span className="error">{patientErrors.email}</span>
                  )}
                </motion.div>
                <motion.div variants={itemVariants} className="form-group dob">
                  <label>Date of Birth</label>
                  <div className="dob-inputs">
                    <div className="dob-input">
                      <select
                        name="month"
                        value={patientData.dateOfBirth.month}
                        onChange={(e) =>
                          handlePatientDateChange("month", e.target.value)
                        }
                        onBlur={() => handlePatientDateBlur("month")}
                      >
                        <option value="">Month</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(0, i).toLocaleString("default", {
                              month: "long",
                            })}
                          </option>
                        ))}
                      </select>
                      {patientErrors.month && (
                        <span className="error">{patientErrors.month}</span>
                      )}
                    </div>
                    <div className="dob-input">
                      <input
                        type="text"
                        name="date"
                        value={patientData.dateOfBirth.date}
                        onChange={(e) =>
                          handlePatientDateChange("date", e.target.value)
                        }
                        onBlur={() => handlePatientDateBlur("date")}
                        placeholder="Day"
                        maxLength="2"
                        style={{
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                      />
                      {patientErrors.date && (
                        <span className="error">{patientErrors.date}</span>
                      )}
                    </div>
                    <div className="dob-input">
                      <input
                        type="text"
                        name="year"
                        value={patientData.dateOfBirth.year}
                        onChange={(e) =>
                          handlePatientDateChange("year", e.target.value)
                        }
                        onBlur={() => handlePatientDateBlur("year")}
                        placeholder="Year"
                        maxLength="4"
                        style={{
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                      />
                      {patientErrors.year && (
                        <span className="error">{patientErrors.year}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="form-group password-group"
                >
                  <label>Password</label>
                  <div className="password-wrapper">
                    <input
                      type={showPatientPassword ? "text" : "password"}
                      name="password"
                      value={patientData.password}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter your password"
                    />
                    <span
                      className={`toggle-password ${
                        patientData.password ? "visible" : "hidden"
                      }`}
                      onClick={() =>
                        setShowPatientPassword(!showPatientPassword)
                      }
                    >
                      {showPatientPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                  {patientErrors.password && (
                    <span className="error">{patientErrors.password}</span>
                  )}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="form-group password-group"
                >
                  <label>Confirm Password</label>
                  <div className="password-wrapper">
                    <input
                      type={showPatientConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={patientData.confirmPassword}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Re-enter your password"
                    />
                    <span
                      className={`toggle-password ${
                        patientData.confirmPassword ? "visible" : "hidden"
                      }`}
                      onClick={() =>
                        setShowPatientConfirmPassword(
                          !showPatientConfirmPassword
                        )
                      }
                    >
                      {showPatientConfirmPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                  {patientErrors.confirmPassword && (
                    <span className="error">
                      {patientErrors.confirmPassword}
                    </span>
                  )}
                </motion.div>
                {patientApiError && (
                  <motion.p className="error-message" variants={itemVariants}>
                    {patientApiError}
                  </motion.p>
                )}
                {patientApiSuccess && (
                  <motion.p className="success-message" variants={itemVariants}>
                    {patientApiSuccess}
                  </motion.p>
                )}
                <motion.div variants={itemVariants}>
                  <motion.button
                    type="submit"
                    className={`continue-button ${
                      patientLoading ||
                      isPatientIncomplete() ||
                      Object.values(patientErrors).some((error) => error)
                        ? "disabled"
                        : ""
                    }`}
                    disabled={
                      patientLoading ||
                      isPatientIncomplete() ||
                      Object.values(patientErrors).some((error) => error)
                    }
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {patientLoading ? "Registering..." : "Continue with email"}
                  </motion.button>
                </motion.div>
              </motion.form>
            )}
          </AnimatePresence>
          <div className="login-section">
            <div className="or-line">
              <ul></ul>
              <p className="login-link">OR</p>
              <ul></ul>
            </div>
            <a
              href="/login"
              className="login-link"
              onClick={(e) => {
                e.preventDefault();
                navigate("/login");
              }}
            >
              Log in to an existing account
            </a>
          </div>
        </div>
      </motion.div>
      {selectedRole && (
        <motion.p
          className="terms"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          By creating an account, you agree to Syndicate Bio's Terms of Service,
          and acknowledge our Privacy Policy.
        </motion.p>
      )}
    </motion.div>
  );
};

console.log("Signup Component Loaded");

export default Signup;
